// storageInventory.ts — the real substrate behind Settings → Data & Storage.
//
// Everything TruXSocial stores on this device lives in localStorage under the
// `truxsocial:` prefix. This module categorises those keys, measures them, and
// clears the safely-clearable ones. The section's numbers and its Clear button
// both run through here, so what the UI reports is what actually exists.

const PREFIX = 'truxsocial:';

/** Category ids in display order. */
export type StorageCategoryId = 'session' | 'preferences' | 'interface' | 'owner' | 'other';

export interface StorageCategory {
  id: StorageCategoryId;
  label: string;
  /** Approximate size in bytes (UTF-16 code units × 2, key + value). */
  bytes: number;
  keys: string[];
  /** Whether "Clear interface state" removes this category. */
  clearable: boolean;
}

/** Interface-state keys: harmless UI memory, safe to clear at any time. */
const INTERFACE_KEYS = new Set([
  `${PREFIX}railCollapsed`,
  `${PREFIX}backdropLatch`,
  `${PREFIX}homeHeroDismissed`,
  `${PREFIX}profileMediaView`,
]);

function categoryOf(key: string): StorageCategoryId {
  if (key === `${PREFIX}accounts`) return 'session';
  if (key.startsWith(`${PREFIX}prefs`)) return 'preferences';
  if (INTERFACE_KEYS.has(key)) return 'interface';
  if (key === `${PREFIX}owner`) return 'owner';
  return 'other';
}

const CATEGORY_META: Record<StorageCategoryId, { label: string; clearable: boolean }> = {
  session: { label: 'Session & accounts', clearable: false },
  preferences: { label: 'Preferences', clearable: false },
  interface: { label: 'Interface state', clearable: true },
  owner: { label: 'Owner console cache', clearable: true },
  other: { label: 'Other', clearable: true },
};

const ORDER: StorageCategoryId[] = ['session', 'preferences', 'interface', 'owner', 'other'];

/** Approximate stored size of one entry. JS strings are UTF-16 → 2 bytes/unit. */
function entryBytes(key: string, value: string): number {
  return (key.length + value.length) * 2;
}

/**
 * Scan localStorage and return every TruXSocial category that currently holds
 * data, in display order. Categories with no keys are omitted.
 */
export function storageInventory(): StorageCategory[] {
  const byId = new Map<StorageCategoryId, StorageCategory>();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    const value = localStorage.getItem(key) ?? '';
    const id = categoryOf(key);
    const meta = CATEGORY_META[id];
    const cat =
      byId.get(id) ?? { id, label: meta.label, bytes: 0, keys: [], clearable: meta.clearable };
    cat.bytes += entryBytes(key, value);
    cat.keys.push(key);
    byId.set(id, cat);
  }
  return ORDER.map((id) => byId.get(id)).filter((c): c is StorageCategory => c !== undefined);
}

export function totalBytes(inventory: StorageCategory[]): number {
  return inventory.reduce((sum, c) => sum + c.bytes, 0);
}

/**
 * Remove every clearable category (interface state, owner cache, stray keys).
 * Session and preferences are never touched — signing out and Appearance →
 * Reset own those lifecycles. Returns the number of keys removed.
 */
export function clearInterfaceState(): number {
  const removable = storageInventory()
    .filter((c) => c.clearable)
    .flatMap((c) => c.keys);
  for (const key of removable) localStorage.removeItem(key);
  return removable.length;
}

/** "1.4 KB" / "312 B" — bytes are approximate, keep the label short. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}
